<template>
  <div class="quarters-page">
    <!-- MOTD Modal Component -->
    <MotdModal :route="$route.path" />

    <div class="title-bar">
      <span
        >Quarter Tracker <small>({{ filteredRecords.length }})</small></span
      >
      <div class="title-buttons">
        <button class="title-button" @click="goBack">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button
          v-if="isMobile"
          @click="$router.push('/mhome')"
          class="title-button"
        >
          <i class="fas fa-home"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="title-button">
          <i class="fas fa-close"></i>
        </button>
      </div>
    </div>

    <div class="content-wrapper">
      <div class="quarters-container">
        <!-- Search Bar Area -->
        <div class="search-bar-area">
          <div class="search-input-wrapper">
            <input
              ref="searchInput"
              v-model="searchQuery"
              placeholder="Search by address or date..."
              @keydown.esc="clearSearch"
              @keydown.tab.prevent="focusDateInput"
              class="search-input"
            />
            <button @click="clearSearch" class="clear-search-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <select v-model="selectedYear" class="year-select">
            <option value="all">All Years</option>
            <option v-for="year in availableYears" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
          <button
            @click="toggleDeletedView"
            :class="{ 'show-deleted-btn': true, active: showDeleted }"
          >
            <i class="fas fa-eye-slash"></i>
          </button>
          <button @click="refreshData" class="refresh-btn">
            <i class="fas fa-refresh"></i>
          </button>
          <button
            @click="openChart('dollarsPerDay')"
            class="chart-btn"
            title="Dollars Per Day"
          >
            <i class="fas fa-chart-bar"></i>
          </button>
          <button
            @click="openChart('runningTotal')"
            class="chart-btn"
            title="Running Total"
          >
            <i class="fas fa-chart-line"></i>
          </button>
        </div>

        <!-- Table Area Wrapper -->
        <div class="table-wrapper">
          <!-- Table Header -->
          <div class="table-header">
            <div class="header-cell date-col">Date</div>
            <div class="header-cell address-col">Address</div>
            <div class="header-cell rolls-col">Rolls</div>
            <div class="header-cell coins-col">Coins</div>
            <div class="header-cell amount-col">Amount</div>
            <div class="header-cell deposited-col">Deposited</div>
            <div class="header-cell notes-col">Notes</div>
          </div>

          <!-- Table Body -->
          <div class="table-body">
            <!-- Loading Spinner -->
            <div v-if="isLoading" class="loading-spinner-container">
              <div class="loading-spinner"></div>
              <div class="loading-text">Loading quarters data...</div>
            </div>

            <div
              v-for="record in filteredRecords"
              :key="record.id"
              :class="{
                'table-row': true,
                selected: selectedRecord && selectedRecord.id === record.id,
                deleted: record.deleted === 1,
              }"
              @dblclick="selectRecord(record)"
            >
              <div class="cell date-col" @click="filterByDate(record)">
                {{ formatDate(record.date) }}
              </div>
              <div
                class="cell address-col"
                v-html="highlightMatch(searchQuery, record.address)"
                @dblclick="filterByAddress(record)"
              ></div>
              <div class="cell rolls-col">{{ record.rolls }}</div>
              <div class="cell coins-col">{{ record.coins }}</div>
              <div class="cell amount-col">
                ${{ formatAmount(record.calculatedAmount) }}
              </div>
              <div
                class="cell deposited-col"
                @dblclick.stop="toggleDeposited(record)"
              >
                <i
                  :class="{
                    fas: true,
                    'fa-check': record.deposited === 1,
                    'fa-times': record.deposited === 0,
                    'deposited-check': record.deposited === 1,
                    'not-deposited': record.deposited === 0,
                  }"
                ></i>
              </div>
              <div class="cell notes-col">{{ record.notes || '' }}</div>
            </div>
            <div v-if="filteredRecords.length === 0" class="no-records">
              No records found
            </div>
          </div>

          <!-- Table Footer -->
          <div class="table-footer">
            <div class="footer-cell date-col">{{ filteredRecords.length }}</div>
            <div class="footer-cell address-col"></div>
            <div class="footer-cell rolls-col">{{ totalRolls }}</div>
            <div class="footer-cell coins-col">{{ totalCoins }}</div>
            <div class="footer-cell amount-col">
              ${{ formatAmount(totalAmount) }}
            </div>
            <div class="footer-cell deposited-col"></div>
            <div class="footer-cell notes-col"></div>
          </div>
        </div>

        <!-- Input Area -->
        <div class="input-area">
          <input
            ref="dateInput"
            v-model="inputDate"
            placeholder="MM/DD/YY"
            class="input-field date-input"
            @keydown.tab.prevent="focusNext"
            @keydown.enter="saveRecord"
            @input="formatDateInput"
            maxlength="8"
          />
          <div class="address-input-wrapper">
            <input
              ref="addressInput"
              v-model="inputAddress"
              placeholder="Address"
              class="input-field address-input"
              @keydown.tab.prevent="handleAddressTab"
              @keydown.enter="saveRecord"
              @input="filterAddresses"
              @focus="showAddressDropdown = true"
            />
            <div
              v-if="showAddressDropdown && filteredAddresses.length > 0"
              class="address-dropdown"
            >
              <div
                v-for="property in filteredAddresses"
                :key="property.Id"
                class="address-option"
                @click="selectAddress(property)"
              >
                {{ property.address }}
              </div>
            </div>
          </div>
          <input
            ref="rollsInput"
            v-model.number="inputRolls"
            placeholder="Rolls"
            type="number"
            min="0"
            class="input-field rolls-input"
            @keydown.tab.prevent="focusNext"
            @keydown.enter="saveRecord"
          />
          <input
            ref="coinsInput"
            v-model.number="inputCoins"
            placeholder="Coins"
            type="number"
            min="0"
            class="input-field coins-input"
            @keydown.tab.prevent="focusNext"
            @keydown.enter="saveRecord"
          />
          <input
            ref="notesInput"
            v-model="inputNotes"
            placeholder="Notes"
            class="input-field notes-input"
            @keydown.tab.prevent="saveRecord"
            @keydown.enter="saveRecord"
            maxlength="80"
          />
          <button @click="deleteRecord" class="delete-btn" v-if="isEditMode">
            <i class="fas fa-trash"></i>
          </button>
          <button @click="clearInputs" class="clear-btn" v-if="!isEditMode">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Chart Modal -->
    <div
      v-if="chartType"
      class="chart-modal-overlay"
      @click.self="chartType = null"
    >
      <div class="chart-modal">
        <div class="chart-modal-header">
          <span>{{
            chartType === 'dollarsPerDay'
              ? 'Dollars Per Day Trend'
              : 'Running Total'
          }}</span>
          <button @click="chartType = null" class="chart-close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="chart-modal-body">
          <div ref="chartContainer" class="chart-container"></div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Popup -->
    <div v-if="showDeleteConfirm" class="delete-confirm-popup">
      <div class="delete-confirm-content">
        <p>Are you sure you want to delete this record?</p>
        <div class="delete-confirm-buttons">
          <button @click="confirmDelete" class="confirm-btn">Yes</button>
          <button @click="cancelDelete" class="cancel-btn">No</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import Plotly from 'plotly.js-dist';
import MotdModal from './MotdModal.vue';
import eventBus from './eventBus.js';

export default {
  name: 'QuarterTracker',
  components: {
    MotdModal,
  },
  data() {
    return {
      records: [],
      filteredRecords: [],
      properties: [],
      searchQuery: '',
      selectedYear: new Date().getFullYear().toString(),
      showDeleted: false,
      isLoading: false, // Loading state for data fetching

      // Input fields
      inputDate: '',
      inputAddress: '',
      inputRolls: '',
      inputCoins: '',
      inputNotes: '',

      // Address dropdown
      showAddressDropdown: false,
      filteredAddresses: [],
      selectedPropertyId: null,

      // Selection and editing
      selectedRecord: null,
      isEditMode: false,

      // Delete confirmation
      showDeleteConfirm: false,
      recordToDelete: null,

      // Chart modal
      chartType: null, // 'dollarsPerDay' or 'runningTotal'

      // Computed values
      isMobile: window.innerWidth <= 768,
    };
  },

  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let year = 2023; year <= currentYear; year++) {
        years.push(year.toString());
      }
      return years.reverse();
    },

    totalRolls() {
      return this.filteredRecords.reduce(
        (sum, record) => sum + (record.rolls || 0),
        0,
      );
    },

    totalCoins() {
      return this.filteredRecords.reduce(
        (sum, record) => sum + (record.coins || 0),
        0,
      );
    },

    totalAmount() {
      return this.filteredRecords.reduce(
        (sum, record) => sum + (parseFloat(record.calculatedAmount) || 0),
        0,
      );
    },
  },

  watch: {
    searchQuery() {
      this.filterRecords();
    },

    selectedYear() {
      this.fetchRecords();
    },

    showDeleted() {
      this.fetchRecords();
    },

    chartType(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.renderChart(newVal);
        });
      }
    },
  },

  methods: {
    async fetchRecords() {
      console.log('🔄 QuarterTracker: Fetching records...');
      this.isLoading = true;
      try {
        const params = {
          year: this.selectedYear,
          showDeleted: this.showDeleted,
        };

        console.log(
          '🔄 QuarterTracker: Making API request with params:',
          params,
        );
        const response = await axios.get('/api/quarters', { params });
        console.log(
          '✅ QuarterTracker: Received records:',
          response.data.length,
          'records',
        );
        // Sort records by date ascending (oldest first, newest at bottom)
        this.records = response.data.sort(
          (a, b) => new Date(a.date) - new Date(b.date),
        );
        this.filterRecords();
        this.scrollToBottom();
      } catch (error) {
        console.error('❌ QuarterTracker: Error fetching records:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchProperties() {
      try {
        const response = await axios.get('/api/quarters/properties');
        this.properties = response.data;
        this.filteredAddresses = [...this.properties];
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    },

    filterRecords() {
      if (!this.searchQuery.trim()) {
        this.filteredRecords = [...this.records];
        this.scrollToBottom();
        return;
      }

      const query = this.searchQuery.toLowerCase();
      this.filteredRecords = this.records.filter((record) => {
        const address = record.address?.toLowerCase() || '';
        const date = this.formatDate(record.date).toLowerCase();
        return address.includes(query) || date.includes(query);
      });
    },

    filterByDate(record) {
      const formattedDate = this.formatDate(record.date);
      this.searchQuery = formattedDate;
      this.$refs.searchInput.focus();
    },

    filterByAddress(record) {
      this.searchQuery = record.address;
      this.$refs.searchInput.focus();
    },

    filterAddresses() {
      if (!this.inputAddress.trim()) {
        this.filteredAddresses = [...this.properties];
        return;
      }

      const query = this.inputAddress.toLowerCase();
      this.filteredAddresses = this.properties.filter((property) =>
        property.address.toLowerCase().includes(query),
      );

      // Auto-select if only one match
      if (this.filteredAddresses.length === 1) {
        this.selectAddress(this.filteredAddresses[0]);
      }
    },

    selectAddress(property) {
      this.inputAddress = property.address;
      this.selectedPropertyId = property.Id;
      this.showAddressDropdown = false;
      this.filteredAddresses = [...this.properties];

      // Focus on rolls input after address selection
      this.$nextTick(() => {
        if (this.$refs.rollsInput) {
          this.$refs.rollsInput.focus();
        }
      });
    },

    handleAddressTab() {
      // If there's exactly one filtered address, use it
      if (this.filteredAddresses.length === 1) {
        this.selectAddress(this.filteredAddresses[0]);
      } else {
        // Otherwise proceed with normal tab behavior
        this.focusNext();
      }
    },

    selectRecord(record) {
      this.selectedRecord = record;
      this.isEditMode = true;

      // Populate input fields
      this.inputDate = this.formatDateForInput(record.date);
      this.inputAddress = record.address;
      this.selectedPropertyId = record.propertyId;
      this.inputRolls = record.rolls;
      this.inputCoins = record.coins;
      this.inputNotes = record.notes || '';
    },

    formatDateInput() {
      // Auto-format date as user types (MM/DD/YY)
      let value = this.inputDate.replace(/\D/g, '');
      if (value.length >= 3 && value.length <= 4) {
        value = value.substring(0, 2) + '/' + value.substring(2);
      } else if (value.length >= 5) {
        value =
          value.substring(0, 2) +
          '/' +
          value.substring(2, 4) +
          '/' +
          value.substring(4, 6);
      }
      this.inputDate = value;
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear().toString().substr(2)}`;
    },

    formatDateForInput(date) {
      if (!date) return this.formatDate(new Date());
      return this.formatDate(date);
    },

    formatAmount(amount) {
      return parseFloat(amount || 0).toFixed(2);
    },

    parseDateInput(dateStr) {
      // Convert MM/DD/YY to YYYY-MM-DD format
      const parts = dateStr.split('/');
      if (parts.length !== 3) return null;

      let year = parseInt(parts[2]);
      if (year < 50) year += 2000;
      else if (year < 100) year += 1900;

      return `${year}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
    },

    async saveRecord() {
      if (!this.inputDate || !this.inputAddress || !this.selectedPropertyId) {
        alert('Please fill in all required fields');
        return;
      }

      const dateFormatted = this.parseDateInput(this.inputDate);
      if (!dateFormatted) {
        alert('Please enter a valid date (MM/DD/YY)');
        return;
      }

      const recordData = {
        propertyId: this.selectedPropertyId,
        date: dateFormatted,
        rolls: this.inputRolls || 0,
        coins: this.inputCoins || 0,
        deposited: false,
        notes: this.inputNotes || null,
      };

      try {
        if (this.isEditMode && this.selectedRecord) {
          // Update existing record
          await axios.put(
            `/api/quarters/${this.selectedRecord.id}`,
            recordData,
          );
        } else {
          // Create new record
          await axios.post('/api/quarters', recordData);
        }

        this.clearInputs('address', false); // Don't reset date, focus on address
        await this.fetchRecords();
        this.scrollToBottom();
      } catch (error) {
        console.error('Error saving record:', error);
        alert('Error saving record');
      }
    },

    deleteRecord() {
      if (!this.selectedRecord) return;
      this.recordToDelete = this.selectedRecord;
      this.showDeleteConfirm = true;
    },

    async confirmDelete() {
      if (!this.recordToDelete) return;

      try {
        await axios.delete(`/api/quarters/${this.recordToDelete.id}`);
        this.clearInputs();
        await this.fetchRecords();
      } catch (error) {
        console.error('Error deleting record:', error);
        alert('Error deleting record');
      }

      this.cancelDelete();
    },

    cancelDelete() {
      this.showDeleteConfirm = false;
      this.recordToDelete = null;
    },

    async toggleDeposited(record) {
      try {
        const newDepositedStatus = record.deposited === 1 ? 0 : 1;

        // Format date to YYYY-MM-DD format for database
        const date = new Date(record.date);
        const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

        // Use PUT endpoint with full record data, just updating deposited status
        const recordData = {
          propertyId: record.propertyId,
          date: formattedDate,
          rolls: record.rolls,
          coins: record.coins,
          deposited: newDepositedStatus === 1,
          notes: record.notes || null,
        };

        await axios.put(`/api/quarters/${record.id}`, recordData);

        // Update local record
        record.deposited = newDepositedStatus;
      } catch (error) {
        console.error('Error toggling deposited status:', error);
      }
    },

    focusNext(event) {
      const inputs = [
        'dateInput',
        'addressInput',
        'rollsInput',
        'coinsInput',
        'notesInput',
      ];
      const currentIndex = inputs.findIndex(
        (input) => this.$refs[input] === event.target,
      );
      if (currentIndex < inputs.length - 1) {
        this.$refs[inputs[currentIndex + 1]].focus();
      } else {
        this.saveRecord();
      }
    },

    clearSearch() {
      this.searchQuery = '';
      this.$refs.searchInput.focus();
    },

    focusDateInput() {
      this.$nextTick(() => {
        if (this.$refs.dateInput) {
          this.$refs.dateInput.focus();
        }
      });
    },

    clearInputs(focusTarget = 'date', resetDate = true) {
      // Set date to today only if resetDate is true
      if (resetDate) {
        this.inputDate = this.formatDateForInput(new Date());
      }
      // Clear other inputs
      this.inputAddress = '';
      this.inputRolls = '';
      this.inputCoins = '';
      this.inputNotes = '';
      // Clear selection and editing state
      this.selectedRecord = null;
      this.isEditMode = false;
      this.selectedPropertyId = null;
      this.showAddressDropdown = false;
      // Focus on specified input
      this.$nextTick(() => {
        setTimeout(() => {
          if (focusTarget === 'address' && this.$refs.addressInput) {
            this.$refs.addressInput.focus();
          } else if (this.$refs.dateInput) {
            this.$refs.dateInput.focus();
          }
        }, 50);
      });
    },

    toggleDeletedView() {
      this.showDeleted = !this.showDeleted;
    },

    async refreshData() {
      await Promise.all([this.fetchProperties(), this.fetchRecords()]);
    },

    openChart(type) {
      this.chartType = type;
    },

    renderChart(chartType) {
      // Use filtered records (respects any active search filter)
      const allRecords = this.filteredRecords
        .filter((r) => r.deleted !== 1)
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      if (allRecords.length < 2) {
        // Need at least 2 records to calculate
        return;
      }

      // Group records by date and sum amounts
      const dateGroups = {};
      allRecords.forEach((record) => {
        const dateKey = record.date.split('T')[0]; // Get just the date part
        if (!dateGroups[dateKey]) {
          dateGroups[dateKey] = 0;
        }
        dateGroups[dateKey] += parseFloat(record.calculatedAmount) || 0;
      });

      // Convert to sorted array of {date, total}
      const sortedDates = Object.keys(dateGroups)
        .sort((a, b) => new Date(a) - new Date(b))
        .map((date) => ({
          date: new Date(date),
          total: dateGroups[date],
        }));

      let xValues, yValues, trace, layout;

      if (chartType === 'runningTotal') {
        // Running total chart
        let runningTotal = 0;
        const chartData = sortedDates.map((item) => {
          runningTotal += item.total;
          return {
            date: item.date,
            total: item.total,
            runningTotal: runningTotal,
          };
        });

        xValues = chartData.map((item) => {
          const d = item.date;
          return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${String(d.getFullYear()).slice(-2)}`;
        });
        yValues = chartData.map((item) => item.runningTotal.toFixed(2));

        trace = {
          x: xValues,
          y: yValues,
          type: 'bar',
          hovertemplate:
            '<b>%{x}</b><br>Running Total: $%{y}<br>%{customdata}<extra></extra>',
          customdata: chartData.map(
            (item) => `This date: $${item.total.toFixed(2)}`,
          ),
          marker: {
            color: '#7cb5a8',
            line: {
              color: '#5a9a8c',
              width: 1.5,
            },
          },
        };

        layout = {
          margin: { t: 20, l: 60, r: 20, b: 60 },
          height: 350,
          hoverlabel: {
            font: { size: 11, color: '#eee' },
            bgcolor: '#333',
          },
          font: { size: 12, family: 'Arial, sans-serif' },
          xaxis: {
            type: 'category',
            tickangle: -90,
            automargin: true,
            title: {
              text: 'Collection Date',
              standoff: 20,
            },
          },
          yaxis: {
            automargin: true,
            title: 'Running Total ($)',
          },
        };
      } else {
        // Dollars per day chart (original)
        const chartData = [];
        for (let i = 1; i < sortedDates.length; i++) {
          const current = sortedDates[i];
          const previous = sortedDates[i - 1];
          const daysDiff = Math.max(
            1,
            Math.round((current.date - previous.date) / (1000 * 60 * 60 * 24)),
          );
          const dollarsPerDay = current.total / daysDiff;

          chartData.push({
            date: current.date,
            dollarsPerDay: dollarsPerDay,
            total: current.total,
            days: daysDiff,
          });
        }

        xValues = chartData.map((item) => {
          const d = item.date;
          return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${String(d.getFullYear()).slice(-2)}`;
        });
        yValues = chartData.map((item) => item.dollarsPerDay.toFixed(2));

        trace = {
          x: xValues,
          y: yValues,
          type: 'bar',
          hovertemplate:
            '<b>%{x}</b><br>$%{y}/day<br>%{customdata}<extra></extra>',
          customdata: chartData.map(
            (item) => `$${item.total.toFixed(2)} over ${item.days} days`,
          ),
          marker: {
            color: '#baa2bd',
            line: {
              color: '#907793',
              width: 1.5,
            },
          },
        };

        layout = {
          margin: { t: 20, l: 50, r: 20, b: 60 },
          height: 350,
          hoverlabel: {
            font: { size: 11, color: '#eee' },
            bgcolor: '#333',
          },
          font: { size: 12, family: 'Arial, sans-serif' },
          xaxis: {
            type: 'category',
            tickangle: -90,
            automargin: true,
            title: {
              text: 'Collection Date',
              standoff: 20,
            },
          },
          yaxis: {
            automargin: true,
            title: '$/Day',
          },
        };
      }

      Plotly.newPlot(this.$refs.chartContainer, [trace], layout, {
        displayModeBar: false,
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const tableBody = this.$el.querySelector('.table-body');
        if (tableBody) {
          setTimeout(() => {
            tableBody.scrollTop = tableBody.scrollHeight;
          }, 100);
        }
      });
    },

    goBack() {
      this.$router.go(-1);
    },

    sendClose() {
      eventBus.emit('requestCloseQuarters');
    },

    handleGlobalKeydown(event) {
      // Clear inputs when ESC is pressed (unless we're in the search input)
      if (event.key === 'Escape' && event.target !== this.$refs.searchInput) {
        this.clearInputs('date', true); // Reset date and focus on date input
      }
    },

    highlightMatch(filter, content) {
      if (!filter) return content;

      // Ensure content is a string to avoid TypeError when non-string values are processed
      if (typeof content !== 'string') {
        return content;
      }

      const query = filter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${query})`, 'gi');
      return content.replace(regex, '<mark>$1</mark>');
    },

    // Close dropdown when clicking outside
    handleClickOutside(event) {
      if (!event.target.closest('.address-input-wrapper')) {
        this.showAddressDropdown = false;
      }
    },

    // Socket event handlers - now using EventBus
    setupSocketConnection() {
      console.log('🔌 QuarterTracker setting up EventBus listeners...');

      // Listen to EventBus events forwarded from NavBar
      eventBus.on('quarterRecordCreated', (newRecord) => {
        console.log(
          '📥 QuarterTracker received quarterRecordCreated via EventBus:',
          newRecord,
        );
        // Add new record to local data if it matches current filters
        if (this.shouldIncludeRecord(newRecord)) {
          this.records.push(newRecord);
          this.filterRecords();
          this.scrollToBottom();
        }
      });

      eventBus.on('quarterRecordUpdated', (updatedRecord) => {
        console.log(
          '📥 QuarterTracker received quarterRecordUpdated via EventBus:',
          updatedRecord,
        );
        // Update existing record in local data
        const index = this.records.findIndex((r) => r.id === updatedRecord.id);
        if (index !== -1) {
          this.records.splice(index, 1, updatedRecord);
        } else {
          // If record doesn't exist locally but matches filters, add it
          if (this.shouldIncludeRecord(updatedRecord)) {
            this.records.push(updatedRecord);
          }
        }
        this.filterRecords();
      });

      eventBus.on('quarterRecordDeleted', (deletedInfo) => {
        console.log(
          '📥 QuarterTracker received quarterRecordDeleted via EventBus:',
          deletedInfo,
        );
        // Remove deleted record from local data
        const index = this.records.findIndex((r) => r.id === deletedInfo.id);
        if (index !== -1) {
          this.records.splice(index, 1);
        }
        this.filterRecords();
      });

      console.log('🔌 QuarterTracker EventBus listeners registered');
    },

    shouldIncludeRecord(record) {
      // Check if record matches current year filter
      if (this.selectedYear !== 'all') {
        const recordYear = new Date(record.date).getFullYear().toString();
        if (recordYear !== this.selectedYear) {
          return false;
        }
      }

      // Check if record matches deleted filter
      if (!this.showDeleted && record.deleted === 1) {
        return false;
      }

      return true;
    },
  },

  async mounted() {
    console.log('🚀 QuarterTracker: Component mounted, initializing...');

    // Clear and set default inputs
    this.clearInputs();

    // TODO: Setup socket connection for real-time updates after data loads
    // this.setupSocketConnection();

    // Fetch initial data
    console.log('🔄 QuarterTracker: Fetching initial data...');
    await Promise.all([this.fetchProperties(), this.fetchRecords()]);
    console.log('✅ QuarterTracker: Initial data loaded');

    // Setup socket connection after initial data loads
    this.setupSocketConnection();

    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside);

    // Add ESC key listener for clearing inputs
    document.addEventListener('keydown', this.handleGlobalKeydown);

    // Set document title
    document.title = 'Quarter Tracker';
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleGlobalKeydown);

    // Remove EventBus listeners
    eventBus.off('quarterRecordCreated');
    eventBus.off('quarterRecordUpdated');
    eventBus.off('quarterRecordDeleted');
  },
};
</script>

<style scoped>
.quarters-page {
  padding: 0px;
  width: 100%;
  max-width: 920px;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #f0f0f0;
  border-top-left-radius: 5px;
}

.title-bar {
  background: #6e5875;
  color: #fff;
  font-size: 1.4em;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-left-radius: 5px;
  padding-left: 40px;
}

.title-buttons {
  display: flex;
  gap: 10px;
}

.title-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.title-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 15px;
}

.quarters-container {
  display: flex;
  flex-direction: column;
  height: 92%;
  gap: 10px;
}

.quarters-page .search-bar-area {
  display: flex;
  gap: 8px;
  align-items: center;
}

.quarters-page .search-input-wrapper {
  position: relative;
  width: 500px; /* Date column (90px) + Address column (140px) + Rolls (80px) + Coins (80px) + Amount (100px) + gaps */
  flex-shrink: 0;
}

.quarters-page .search-input {
  width: 80%;
  padding: 8px 35px 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

.quarters-page .clear-search-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
}

.quarters-page .year-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  background: white;
  width: 120px; /* Match rolls column width */
  flex-shrink: 0;
}

.quarters-page .show-deleted-btn {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  width: 80px; /* Match deposited column width */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.2s;
}

.quarters-page .show-deleted-btn.active {
  background: #333;
  color: white;
}

.quarters-page .refresh-btn {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.2s;
  color: #666;
}

.quarters-page .refresh-btn:hover {
  background: #f8f9fa;
  color: #333;
}

.quarters-page .chart-btn {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.2s;
  color: #666;
}

.quarters-page .chart-btn:hover {
  background: #f8f9fa;
  color: #333;
}

/* Chart Modal Styles */
.chart-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.chart-modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.chart-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #a080a8;
  color: white;
  border-radius: 8px 8px 0 0;
  font-weight: 600;
}

.chart-close-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 16px;
  padding: 4px 8px;
}

.chart-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.chart-modal-body {
  padding: 16px;
}

.chart-container {
  width: 100%;
  min-height: 350px;
}

.quarters-page .table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  overflow: hidden;
  min-height: 0;
}

.quarters-page .table-header,
.quarters-page .table-footer {
  display: flex;
  background: #e9ecef;
  border-bottom: 2px solid #dee2e6;
  font-weight: bold;
}

.quarters-page .table-footer {
  border-bottom: none;
  border-top: 2px solid #dee2e6;
}

.quarters-page .table-body {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.table-body::-webkit-scrollbar {
  display: none;
}

.quarters-page .header-cell,
.quarters-page .cell,
.quarters-page .footer-cell {
  padding: 3px 8px;
  border-right: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  font-size: 13px;
}

.quarters-page .header-cell:last-child,
.quarters-page .cell:last-child,
.quarters-page .footer-cell:last-child {
  border-right: none;
}

.quarters-page .date-col {
  width: 90px;
  min-width: 90px;
}
.quarters-page .address-col {
  width: 140px;
  min-width: 140px;
}
.quarters-page .rolls-col {
  width: 80px;
  min-width: 80px;
  text-align: center;
  justify-content: center;
}
.quarters-page .coins-col {
  width: 80px;
  min-width: 80px;
  text-align: center;
  justify-content: center;
}
.quarters-page .amount-col {
  width: 100px;
  min-width: 100px;
  text-align: right;
  justify-content: flex-end;
}
.quarters-page .deposited-col {
  width: 80px;
  min-width: 80px;
  text-align: center;
  justify-content: center;
}
.quarters-page .notes-col {
  width: 240px;
  min-width: 240px;
}

.quarters-page .table-row {
  display: flex;
  border-bottom: 1px solid #dee2e6;
  cursor: pointer;
  transition: background-color 0.2s;
}

.quarters-page .table-row:nth-child(even) {
  background: #f8f9fa;
}

.quarters-page .table-row:nth-child(odd) {
  background: white;
}

.table-row:hover {
  background: #e9ecef !important;
}

.table-row.selected {
  background: #e3f2fd;
}

.table-row.deleted {
  background: #ffebee;
  opacity: 0.7;
}

.deposited-check {
  color: #28a745;
  cursor: pointer;
}

.not-deposited {
  color: #dc3545;
  cursor: pointer;
}

.no-records {
  padding: 40px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.quarters-page .input-area {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 12px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  position: relative;
}

.quarters-page .input-field {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  flex-shrink: 0;
}

.quarters-page .date-input {
  width: 90px;
}
.quarters-page .address-input {
  width: 120px;
}
.quarters-page .rolls-input {
  width: 80px;
}
.quarters-page .coins-input {
  width: 80px;
}
.quarters-page .notes-input {
  width: 210px;
}

.quarters-page .address-input-wrapper {
  position: relative;
  width: 140px;
  flex-shrink: 0;
}

.quarters-page .address-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.quarters-page .address-dropdown::-webkit-scrollbar {
  display: none;
}

.quarters-page .address-option {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.quarters-page .address-option:hover {
  background: #f8f9fa;
}

.quarters-page .address-option:last-child {
  border-bottom: none;
}

.quarters-page .delete-btn,
.quarters-page .clear-btn {
  padding: 8px 12px;
  border: 1px solid #dc3545;
  background: #dc3545;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.quarters-page .clear-btn {
  border-color: #6c757d;
  background: #6c757d;
}

.quarters-page .delete-btn:hover {
  background: #c82333;
}

.quarters-page .clear-btn:hover {
  background: #5a6268;
}

.delete-confirm-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.delete-confirm-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.delete-confirm-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 15px;
}

.confirm-btn,
.cancel-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.confirm-btn {
  background: #dc3545;
  color: white;
}

.cancel-btn {
  background: #6c757d;
  color: white;
}

.confirm-btn:hover {
  background: #c82333;
}

.cancel-btn:hover {
  background: #5a6268;
}

/* Loading Spinner Styles */
.loading-spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 15px;
}

.loading-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #a080a8;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #666;
  font-size: 14px;
  font-weight: 500;
}
</style>
